import { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext.jsx';
import api from '../../services/api.js';
import Button from '../../components/common/Button.jsx';
import Logo from '../../components/common/Logo.jsx';

const PLAN_COLORS = { FREE: '#6B7280', STARTER: '#3B82F6', PRO: '#8B5CF6', BUSINESS: '#F59E0B' };

export default function AdminPage() {
  const { user }          = useAuth();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [sysInfo, setSysInfo] = useState(null);
  const [search, setSearch]   = useState('');
  const [page, setPage]       = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [tab, setTab]           = useState('stats');
  const [loading, setLoading]   = useState(true);
  const [investigate, setInvestigate] = useState(null); // { userId, email, name }
  const [invData, setInvData]   = useState(null);
  const [invTab, setInvTab]     = useState('alerts');
  const [invLoading, setInvLoading] = useState(false);

  useEffect(() => { loadStats(); loadSystem(); }, []);
  useEffect(() => { if (tab === 'users') loadUsers(); }, [tab, page, search]);

  const loadStats = () => {
    api.get('/admin/stats').then(r => setStats(r.data.data)).catch(() => {}).finally(() => setLoading(false));
  };

  const loadUsers = () => {
    api.get(`/admin/users?page=${page}&limit=15&q=${search}`)
      .then(r => { setUsers(r.data.data.users); setTotalPages(r.data.data.pages); })
      .catch(() => {});
  };

  const loadSystem = () => {
    api.get('/admin/system').then(r => setSysInfo(r.data.data)).catch(() => {});
  };

  const toggleActive = async (userId, isActive) => {
    await api.patch(`/admin/users/${userId}/toggle-active`);
    setUsers(u => u.map(x => x.id === userId ? { ...x, isActive: !x.isActive } : x));
    toast.success(isActive ? 'Utilisateur banni' : 'Utilisateur activé');
  };

  const changePlan = async (userId, plan) => {
    await api.patch(`/admin/users/${userId}/plan`, { plan });
    setUsers(u => u.map(x => x.id === userId ? { ...x, plan } : x));
    toast.success(`Plan mis à jour : ${plan}`);
  };

  const openInvestigation = async (u) => {
    setInvestigate(u);
    setInvLoading(true);
    setInvTab('alerts');
    try {
      const [alerts, recordings] = await Promise.all([
        api.get(`/admin/users/${u.id}/alerts`),
        api.get(`/admin/users/${u.id}/recordings`),
      ]);
      setInvData({
        alerts:     alerts.data.data.alerts || [],
        recordings: recordings.data.data.recordings || [],
      });
    } catch { toast.error('Erreur chargement données'); }
    finally { setInvLoading(false); }
  };

  const formatUptime = (s) => {
    const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60);
    return `${h}h ${m}m`;
  };

  const tabs = [
    { id: 'stats',  label: '📊 Statistiques' },
    { id: 'users',  label: '👥 Utilisateurs' },
    { id: 'system', label: '⚙️ Système' },
  ];

  const refresh = () => {
    setLoading(true);
    loadStats();
    loadSystem();
    if (tab === 'users') loadUsers();
    toast.success('Données actualisées');
  };

  return (
    <>
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 'var(--text-2xl)', fontWeight: 'var(--font-bold)' }}>Administration</h1>
          <p style={{ fontSize: 'var(--text-xs)', color: 'var(--elvyra-text-muted)' }}>ELVYRA · DEKANI SOFTWARE</p>
        </div>
        <button onClick={refresh} style={{
          display: 'flex', alignItems: 'center', gap: 6,
          padding: '8px 16px', borderRadius: 8, border: '1px solid var(--elvyra-border)',
          background: 'transparent', color: 'var(--elvyra-text-secondary)',
          cursor: 'pointer', fontSize: 13, fontWeight: 600,
        }}>
          🔄 Actualiser
        </button>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 24, borderBottom: '1px solid var(--elvyra-border)', paddingBottom: 0 }}>
        {tabs.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            style={{
              padding: '8px 16px', background: 'none', border: 'none', cursor: 'pointer',
              fontSize: 'var(--text-sm)', fontWeight: 600, borderBottom: '2px solid',
              borderColor: tab === t.id ? 'var(--elvyra-accent)' : 'transparent',
              color: tab === t.id ? 'var(--elvyra-accent)' : 'var(--elvyra-text-muted)',
              marginBottom: -1,
            }}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Statistiques */}
      {tab === 'stats' && stats && (
        <div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12, marginBottom: 24 }}>
            {[
              { label: 'Utilisateurs',  value: stats.users.total,        icon: '👥', sub: `${stats.users.active} actifs` },
              { label: 'Caméras',       value: stats.devices.total,      icon: '📹', sub: `${stats.devices.online} en ligne`, color: stats.devices.online > 0 ? '#00C896' : undefined },
              { label: 'Alertes',       value: stats.alerts.total,       icon: '🔔', sub: `${stats.alerts.unread} non lues`,  color: stats.alerts.unread > 0 ? '#FF3B5C' : undefined },
              { label: 'Enregistrements', value: stats.recordings.total, icon: '📼', sub: 'total' },
            ].map(s => (
              <div key={s.label} className="card" style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 28, marginBottom: 8 }}>{s.icon}</div>
                <div style={{ fontSize: 'var(--text-2xl)', fontWeight: 700, color: s.color || 'var(--elvyra-accent)' }}>{s.value}</div>
                <div style={{ fontSize: 'var(--text-xs)', color: 'var(--elvyra-text-muted)', marginTop: 4 }}>{s.label}</div>
                <div style={{ fontSize: 10, color: 'var(--elvyra-text-muted)', marginTop: 2 }}>{s.sub}</div>
              </div>
            ))}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            {/* Distribution plans */}
            <div className="card">
              <h3 style={{ fontSize: 'var(--text-sm)', fontWeight: 600, marginBottom: 16 }}>Répartition des plans</h3>
              {Object.entries(stats.planDistrib).map(([plan, count]) => (
                <div key={plan} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                  <span style={{ width: 60, fontSize: 'var(--text-xs)', fontWeight: 600, color: PLAN_COLORS[plan] }}>{plan}</span>
                  <div style={{ flex: 1, background: 'var(--elvyra-surface-2)', borderRadius: 4, height: 8, overflow: 'hidden' }}>
                    <div style={{ height: '100%', background: PLAN_COLORS[plan], width: `${(count / stats.users.total) * 100}%`, transition: 'width 500ms' }} />
                  </div>
                  <span style={{ fontSize: 'var(--text-xs)', color: 'var(--elvyra-text-muted)', width: 24, textAlign: 'right' }}>{count}</span>
                </div>
              ))}
            </div>

            {/* Derniers inscrits */}
            <div className="card">
              <h3 style={{ fontSize: 'var(--text-sm)', fontWeight: 600, marginBottom: 16 }}>Dernières inscriptions</h3>
              {stats.recentUsers.map(u => (
                <div key={u.id} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                  <div style={{ width: 30, height: 30, borderRadius: '50%', background: 'var(--elvyra-surface-2)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14 }}>👤</div>
                  <div style={{ flex: 1 }}>
                    <p style={{ fontSize: 'var(--text-xs)', fontWeight: 500 }}>{u.name}</p>
                    <p style={{ fontSize: 10, color: 'var(--elvyra-text-muted)' }}>{u.email}</p>
                  </div>
                  <span style={{ fontSize: 10, color: PLAN_COLORS[u.plan], fontWeight: 600 }}>{u.plan}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Utilisateurs */}
      {tab === 'users' && (
        <div>
          <div style={{ marginBottom: 16 }}>
            <input
              className="form-input"
              placeholder="Rechercher par nom ou email..."
              value={search}
              onChange={e => { setSearch(e.target.value); setPage(1); }}
              style={{ maxWidth: 360 }}
            />
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--elvyra-border)' }}>
                  {['Utilisateur', 'Plan', 'Caméras', 'Statut', 'Inscription', 'Actions'].map(h => (
                    <th key={h} style={{ padding: '8px 12px', textAlign: 'left', fontSize: 'var(--text-xs)', color: 'var(--elvyra-text-muted)', fontWeight: 600 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {users.map(u => (
                  <tr key={u.id} style={{ borderBottom: '1px solid var(--elvyra-border)', opacity: u.isActive ? 1 : 0.5 }}>
                    <td style={{ padding: '10px 12px' }}>
                      <p style={{ fontSize: 'var(--text-sm)', fontWeight: 500 }}>{u.name}</p>
                      <p style={{ fontSize: 10, color: 'var(--elvyra-text-muted)' }}>{u.email}</p>
                    </td>
                    <td style={{ padding: '10px 12px' }}>
                      <select
                        value={u.plan}
                        onChange={e => changePlan(u.id, e.target.value)}
                        style={{ background: 'var(--elvyra-surface-2)', border: '1px solid var(--elvyra-border)', color: PLAN_COLORS[u.plan], borderRadius: 6, padding: '3px 8px', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>
                        {['FREE', 'STARTER', 'PRO', 'BUSINESS'].map(p => <option key={p} value={p}>{p}</option>)}
                      </select>
                    </td>
                    <td style={{ padding: '10px 12px', fontSize: 'var(--text-sm)', color: 'var(--elvyra-text-secondary)' }}>{u._count.devices}</td>
                    <td style={{ padding: '10px 12px' }}>
                      <span style={{ fontSize: 10, fontWeight: 700, padding: '3px 8px', borderRadius: 20, background: u.isActive ? 'rgba(0,200,150,0.15)' : 'rgba(255,59,92,0.15)', color: u.isActive ? '#00C896' : '#FF3B5C' }}>
                        {u.isActive ? '● ACTIF' : '○ BANNI'}
                      </span>
                    </td>
                    <td style={{ padding: '10px 12px', fontSize: 'var(--text-xs)', color: 'var(--elvyra-text-muted)' }}>
                      {new Date(u.createdAt).toLocaleDateString('fr-FR')}
                    </td>
                    <td style={{ padding: '10px 12px', display: 'flex', gap: 6 }}>
                      <button
                        onClick={() => openInvestigation(u)}
                        style={{ background: 'rgba(0,212,255,0.1)', border: '1px solid var(--elvyra-accent)', color: 'var(--elvyra-accent)', borderRadius: 6, padding: '4px 10px', cursor: 'pointer', fontSize: 11, fontWeight: 600 }}>
                        🔍 Voir
                      </button>
                      <button
                        onClick={() => toggleActive(u.id, u.isActive)}
                        style={{
                          background: 'none', border: `1px solid ${u.isActive ? 'var(--elvyra-danger)' : '#00C896'}`,
                          color: u.isActive ? 'var(--elvyra-danger)' : '#00C896',
                          borderRadius: 6, padding: '4px 10px', cursor: 'pointer', fontSize: 11, fontWeight: 600,
                        }}>
                        {u.isActive ? 'Bannir' : 'Activer'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {totalPages > 1 && (
            <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 16 }}>
              <Button variant="secondary" size="sm" disabled={page === 1} onClick={() => setPage(p => p - 1)}>← Précédent</Button>
              <span style={{ display: 'flex', alignItems: 'center', fontSize: 'var(--text-xs)', color: 'var(--elvyra-text-muted)' }}>Page {page}/{totalPages}</span>
              <Button variant="secondary" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>Suivant →</Button>
            </div>
          )}
        </div>
      )}

      {/* Système */}
      {tab === 'system' && sysInfo && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 16 }}>
          {[
            { label: 'Uptime', value: formatUptime(sysInfo.uptime), icon: '⏱' },
            { label: 'Mémoire heap', value: `${sysInfo.memory.heapUsed} Mo / ${sysInfo.memory.heapTotal} Mo`, icon: '💾' },
            { label: 'RSS mémoire', value: `${sysInfo.memory.rss} Mo`, icon: '🖥' },
            { label: 'Node.js', value: sysInfo.nodeVersion, icon: '⚡' },
            { label: 'Environnement', value: sysInfo.env, icon: '🌍' },
            { label: 'Plateforme', value: sysInfo.platform, icon: '🖧' },
          ].map(s => (
            <div key={s.label} className="card">
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontSize: 24 }}>{s.icon}</span>
                <div>
                  <p style={{ fontSize: 'var(--text-xs)', color: 'var(--elvyra-text-muted)' }}>{s.label}</p>
                  <p style={{ fontSize: 'var(--text-sm)', fontWeight: 600, color: 'var(--elvyra-accent)', marginTop: 2 }}>{s.value}</p>
                </div>
              </div>
            </div>
          ))}
          <button onClick={loadSystem} style={{
            gridColumn: 'span 1',
            background: 'none', border: '1px solid var(--elvyra-border)', borderRadius: 'var(--radius-md)',
            color: 'var(--elvyra-text-secondary)', cursor: 'pointer', padding: 'var(--space-4)',
            fontSize: 'var(--text-sm)',
          }}>
            🔄 Actualiser
          </button>
        </div>
      )}
    </div>

    {/* Modal Investigation */}
    {investigate && (
      <div style={{ position: 'fixed', inset: 0, background: 'rgba(6,14,31,0.95)', backdropFilter: 'blur(8px)', zIndex: 1000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '32px 16px', overflowY: 'auto' }}>
        <div style={{ background: '#0A1628', border: '1px solid #1E3A5F', borderRadius: 16, width: '100%', maxWidth: 720 }}>

          {/* Header */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 24px', borderBottom: '1px solid #1E3A5F' }}>
            <div>
              <h3 style={{ margin: 0, color: 'var(--elvyra-text-primary)', fontSize: 16, fontWeight: 700 }}>
                🔍 Investigation — {investigate.name}
              </h3>
              <p style={{ margin: '4px 0 0', color: 'var(--elvyra-text-muted)', fontSize: 12 }}>{investigate.email}</p>
            </div>
            <button onClick={() => { setInvestigate(null); setInvData(null); }} style={{ background: 'none', border: 'none', color: 'var(--elvyra-text-muted)', cursor: 'pointer', fontSize: 22 }}>×</button>
          </div>

          {/* Tabs */}
          <div style={{ display: 'flex', borderBottom: '1px solid #1E3A5F', padding: '0 24px' }}>
            {[['alerts','🔔 Alertes'], ['recordings','📼 Enregistrements']].map(([id, label]) => (
              <button key={id} onClick={() => setInvTab(id)} style={{
                padding: '10px 16px', background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600,
                borderBottom: `2px solid ${invTab === id ? 'var(--elvyra-accent)' : 'transparent'}`,
                color: invTab === id ? 'var(--elvyra-accent)' : 'var(--elvyra-text-muted)',
                marginBottom: -1,
              }}>{label}</button>
            ))}
          </div>

          {/* Contenu */}
          <div style={{ padding: 24, maxHeight: '60vh', overflowY: 'auto' }}>
            {invLoading ? (
              <div style={{ textAlign: 'center', padding: 40, color: 'var(--elvyra-text-muted)' }}>Chargement...</div>
            ) : !invData ? null : invTab === 'alerts' ? (
              invData.alerts.length === 0 ? (
                <p style={{ color: 'var(--elvyra-text-muted)', fontSize: 13, textAlign: 'center', padding: 32 }}>Aucune alerte</p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {invData.alerts.map(a => (
                    <div key={a.id} style={{ background: '#060E1F', borderRadius: 8, padding: '10px 14px', border: '1px solid #1E3A5F', display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                      <span style={{ fontSize: 18 }}>{a.type === 'MOTION' ? '🎯' : a.type === 'OFFLINE' ? '📴' : '🔊'}</span>
                      <div style={{ flex: 1 }}>
                        <p style={{ margin: 0, fontSize: 13, color: 'var(--elvyra-text-primary)', fontWeight: 600 }}>{a.message}</p>
                        <p style={{ margin: '3px 0 0', fontSize: 11, color: 'var(--elvyra-text-muted)' }}>
                          📹 {a.device?.name}{a.device?.location ? ` — ${a.device.location}` : ''} · {new Date(a.createdAt).toLocaleString('fr-FR')}
                        </p>
                        {a.thumbnail && <img src={a.thumbnail} alt="capture" style={{ marginTop: 6, borderRadius: 6, maxWidth: 180, maxHeight: 100, objectFit: 'cover' }} />}
                      </div>
                    </div>
                  ))}
                </div>
              )
            ) : (
              invData.recordings.length === 0 ? (
                <p style={{ color: 'var(--elvyra-text-muted)', fontSize: 13, textAlign: 'center', padding: 32 }}>Aucun enregistrement</p>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px,1fr))', gap: 12 }}>
                  {invData.recordings.map(r => (
                    <div key={r.id} style={{ background: '#060E1F', borderRadius: 8, overflow: 'hidden', border: '1px solid #1E3A5F' }}>
                      {r.thumbnail && <img src={r.thumbnail} alt="thumb" style={{ width: '100%', height: 100, objectFit: 'cover' }} />}
                      <div style={{ padding: '8px 10px' }}>
                        <p style={{ margin: 0, fontSize: 12, color: 'var(--elvyra-text-primary)', fontWeight: 600 }}>📹 {r.device?.name}</p>
                        <p style={{ margin: '2px 0', fontSize: 10, color: 'var(--elvyra-text-muted)' }}>
                          {new Date(r.startedAt).toLocaleString('fr-FR')} · {r.duration}s
                        </p>
                        {r.signedUrl && r.signedUrl.startsWith('http') && (
                          <a href={r.signedUrl} target="_blank" rel="noreferrer" style={{ display: 'inline-block', marginTop: 4, fontSize: 11, color: 'var(--elvyra-accent)', fontWeight: 600 }}>
                            ▶ Lire le clip
                          </a>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )
            )}
          </div>
        </div>
      </div>
    )}
    </>
  );
}
